import { type Database } from '@databases/model/database';
import { type Entry } from '@databases/model/entry';
import { type Field, FieldType } from '@databases/model/field';
import {
  type UserValue,
  type SelectValue,
  type Value,
  UserType,
  type TextValue,
  type NumberValue,
  type DateValue,
} from '@databases/model/value';
import { unparse } from 'papaparse';

import { type Warnings } from './warnings';

function encodeDatabaseValue(value: Value<FieldType>, field: Field): string | undefined {
  switch (value.type) {
    case FieldType.Text:
      return (value as TextValue).value;
    case FieldType.Number:
      return (value as NumberValue).value.toString();
    case FieldType.Date:
      return (value as DateValue).value;
    case FieldType.Select:
      return (value as SelectValue).value
        .map(
          (value) =>
            field.selectOptions.options.find((option) => option.id === value)?.label
        )
        .filter(Boolean)
        .join(',');
    case FieldType.User:
      return (value as UserValue).value
        .map((user) =>
          user.type === UserType.Referenced ? user.accountId : 'Current User'
        )
        .join(',');
    default:
      return undefined;
  }
}

function encodeDatabaseRow(entry: Entry, database: Database, index: number) {
  const row: Record<string, string> = {
    _id: String(index),
  };
  for (const fieldId of database.fields) {
    const value = entry.getValue(fieldId);
    const field = database.fieldsById[fieldId];

    if (value) {
      row[field.name] = encodeDatabaseValue(value, field) ?? '';
    }
  }
  return row;
}

/**
 * Serializes the contents of a database.
 *
 * This will serialize as a CSV with the following format:
 *
 * _id, Field 1,Field 2,Field 3
 * 1, Value 1,Value 2,Value 3
 * 2, Value 4,Value 5,Value 6
 *
 * An `_id` field is added to the beginning of the row to identify the entry, which uses a 1-based index.
 *
 * @param database The database to serialize the contents of.
 * @returns The serialized contents.
 */
export function serializeDatabaseContents(
  database: Database,
  _warnings: Warnings,
  rowIdToEntryIdMappings: Map<string, string>
): string {
  const rows = database.entries
    .map((entryId, index) => {
      const entry = database.entriesById[entryId];

      if (entry) {
        const row = encodeDatabaseRow(entry, database, index + 1);
        rowIdToEntryIdMappings.set(String(index + 1), entryId);
        return row;
      }

      return undefined;
    })
    .filter(Boolean);
  const columns = Object.values(database.fieldsById).map((field) => field.name);

  return unparse(rows, {
    header: true,
    escapeChar: '\\',
    columns: ['_id', ...columns],
  });
}
